/**
 * Keeps the price shown inside the product page add to cart button in sync with the selected variant and quantity.
 *
 * The theme's <buy-buttons> element rewrites the button label on every variant change, so the price is re-applied
 * after it has run. The quantity selector changes its value with stepUp()/stepDown(), which fire no input event,
 * so clicks on the selector are watched as well.
 */
import { formatMoney } from 'theme';

class BuyButtonsPrice extends HTMLElement {
  #abortController;
  #resizeObserver;
  #unitPrice;
  #available = true;

  connectedCallback() {
    this.#abortController = new AbortController();
    this.#unitPrice = parseInt(this.getAttribute('price'), 10);
    this.#available = this.#button ? !this.#button.disabled : false;

    const signal = this.#abortController.signal,
      form = document.forms[this.getAttribute('form')],
      row = this.closest('.buy-buttons__atc-row');

    form?.addEventListener('variant:change', this.#onVariantChange.bind(this), { signal });
    row?.addEventListener('click', this.#onQuantityInteraction.bind(this), { signal });
    row?.addEventListener('input', this.#onQuantityInteraction.bind(this), { signal });
    row?.addEventListener('change', this.#onQuantityInteraction.bind(this), { signal });

    this.#render();

    if (this.#button) {
      this.#resizeObserver = new ResizeObserver(() => this.#updateWrapping());
      this.#resizeObserver.observe(this.#button);
    }
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#resizeObserver?.disconnect();
  }

  get #button() {
    return this.closest('buy-buttons')?.querySelector('button[type="submit"]');
  }

  get #quantity() {
    const input = document.querySelector(`input[name="quantity"][form="${this.getAttribute('form')}"]`),
      quantity = parseInt(input?.value, 10);

    return Number.isNaN(quantity) || quantity < 1 ? 1 : quantity;
  }

  #onVariantChange(event) {
    const variant = event.detail.variant;

    this.#available = Boolean(variant?.available);
    this.#unitPrice = variant ? variant.price : this.#unitPrice;

    // Let the theme's <buy-buttons> listener rewrite the label first
    queueMicrotask(() => this.#render());
  }

  #onQuantityInteraction(event) {
    if (!event.target.closest('quantity-selector')) {
      return;
    }

    queueMicrotask(() => this.#render());
  }

  #render() {
    const button = this.#button;

    if (!button) {
      return;
    }

    button.querySelector('.buy-buttons__price')?.remove();

    if (!this.#available || Number.isNaN(this.#unitPrice)) {
      return;
    }

    const settings = window.themeVariables.settings,
      format = settings.currencyCodeEnabled ? settings.moneyWithCurrencyFormat : settings.moneyFormat,
      price = document.createElement('span');

    price.className = 'buy-buttons__price';
    price.innerHTML = formatMoney(this.#unitPrice * this.#quantity, format);
    button.append(price);
    this.#updateWrapping();
  }

  // When the price no longer fits beside the label it moves to its own line, where the separator dot is not needed.
  // On desktop it never moves: the font is shrunk until the label and price fit on one line instead.
  #updateWrapping() {
    const button = this.#button,
      price = button?.querySelector('.buy-buttons__price');

    if (!price) {
      button?.style.removeProperty('font-size');
      return;
    }

    price.classList.remove('is-wrapped');
    button.style.removeProperty('font-size');

    if (window.matchMedia('screen and (min-width: 1000px)').matches) {
      this.#fitOnOneLine(button);
      return;
    }

    const label = document.createRange();
    label.selectNodeContents(button);
    label.setEndBefore(price);

    price.classList.toggle('is-wrapped', price.getBoundingClientRect().top >= label.getBoundingClientRect().bottom - 1);
  }

  // The letter spacing is set in em, so it shrinks with the font
  #fitOnOneLine(button) {
    const style = getComputedStyle(button),
      available = button.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight),
      content = document.createRange();

    content.selectNodeContents(button);

    let fontSize = parseFloat(style.fontSize);

    while (content.getBoundingClientRect().width > available && fontSize > 6) {
      fontSize -= 0.5;
      button.style.fontSize = `${fontSize}px`;
    }
  }
}

if (!window.customElements.get('buy-buttons-price')) {
  window.customElements.define('buy-buttons-price', BuyButtonsPrice);
}
